import React from "react";
import moment from "moment";
import { PrintSettingsStateTypes } from "../types/print_settings";
import { getProductBarcode, Product } from "../types/product";
import { getStyles } from "./getStyles";
import { formatRupiah } from "./FormattedPrice";
import RenderBarcode from "../components/RenderBarcode";

export interface PrintLayoutCard {
  id: string | number;
  text: React.ReactNode;
  isShow: boolean;
  className?: string;
  style?: React.CSSProperties;
  group?: string;
}

export interface PrintLabelLayout {
  cards: PrintLayoutCard[];
  lists: PrintLayoutCard[];
}

export interface PrintPriceDisplayOptions {
  showPriceAdjustment: boolean;
  showPriceStrikethrough: boolean;
}

const productName = (data: Product) =>
  data.itemName?.length > 50 ? `${data.itemName.slice(0, 50)}...` : data.itemName;

const formattedDate = (settings: PrintSettingsStateTypes) =>
  moment(settings.customDate || undefined).format(
    settings.dateFormat || "DD MMM YYYY",
  );

const formattedMasterPrice = (
  data: Product,
  options: PrintPriceDisplayOptions,
): React.ReactNode => {
  const masterPrice = formatRupiah(data.itemPrice);
  return options.showPriceStrikethrough ? (
    <span className="price-strikethrough">{masterPrice}</span>
  ) : (
    masterPrice
  );
};

export const createPrintLabelLayout = (
  data: Product,
  settings: PrintSettingsStateTypes,
  options: PrintPriceDisplayOptions = {
    showPriceAdjustment: false,
    showPriceStrikethrough: false,
  },
): PrintLabelLayout => {
  const barcode = getProductBarcode(data);

  return {
    cards: [
      {
        id: 1,
        text: productName(data),
        className: "text-xs font-bold",
        isShow: settings.showNameDisplay,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 2,
        text: barcode,
        className: "text-xs font-bold",
        isShow: settings.showCodeDisplay,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 3,
        text: formattedMasterPrice(data, options),
        className: "text-xl font-bold",
        isShow: settings.showPriceDisplay,
        group: "price",
        style: getStyles(settings.fontPrice, settings.fontPrice),
      },
      {
        id: 4,
        text: formattedDate(settings),
        className: "text-[8px] font-bold",
        isShow: settings.showDateDisplay,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 5,
        text: formatRupiah(data.itemPriceAdjustment ?? 0),
        className: "text-xl font-bold",
        isShow: options.showPriceAdjustment,
        group: "price",
        style: getStyles(settings.fontPrice, settings.fontPrice),
      },
    ],
    lists: [
      {
        id: 1,
        text: (
          <RenderBarcode
            value={barcode}
            settings={settings}
            type={settings.barcodeType}
          />
        ),
        className: "text-xs font-bold",
        isShow: settings.showBarcode,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 2,
        text: productName(data),
        className: "text-xs font-bold",
        isShow: settings.showName,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 3,
        text: formattedDate(settings),
        className: "text-[8px] font-bold",
        isShow: settings.showDate,
        style: getStyles(settings.fontSize, settings.fontSize),
      },
      {
        id: 4,
        text: formattedMasterPrice(data, options),
        className: "text-xs font-bold",
        isShow: settings.showPrice,
        group: "price",
        style: getStyles(settings.fontPrice, settings.fontPrice),
      },
      {
        id: 5,
        text: formatRupiah(data.itemPriceAdjustment ?? 0),
        className: "text-xs font-bold",
        isShow: options.showPriceAdjustment,
        group: "price",
        style: getStyles(settings.fontPrice, settings.fontPrice),
      },
    ],
  };
};

const syncCards = (
  current: PrintLayoutCard[],
  defaults: PrintLayoutCard[],
): PrintLayoutCard[] => {
  const defaultsById = new Map(defaults.map((card) => [card.id, card]));
  const currentIds = new Set(current.map((card) => card.id));
  return [
    ...current.map((card) => ({ ...card, ...defaultsById.get(card.id) })),
    ...defaults.filter((card) => !currentIds.has(card.id)),
  ];
};

export const syncPrintLabelLayout = (
  current: PrintLabelLayout | undefined,
  data: Product,
  settings: PrintSettingsStateTypes,
  options?: PrintPriceDisplayOptions,
): PrintLabelLayout => {
  const defaults = createPrintLabelLayout(data, settings, options);
  if (!current) return defaults;

  return {
    cards: syncCards(current.cards, defaults.cards),
    lists: syncCards(current.lists, defaults.lists),
  };
};
